import React from 'react';
import { CellProps } from 'react-table';
import { fileCabinetRecordId } from '../../services/file-cabinet/file-cabinet.types';
import type {
    FileCabinetApp,
    FileCabinetCapabilities,
    FileCabinetRecord,
    FileCabinetViewKey,
} from '../../services/file-cabinet/file-cabinet.types';
import { FILE_CABINET_VIEWS, FileCabinetView, fileCabinetFilterSchemas } from '../../services/file-cabinet/file-cabinet.views';
import { DataTablePresenter, ITableColumn } from '../data-table/data-table.presenter';
import { FileCabinetCellContext, renderFileCabinetCell } from './file-cabinet-cells';
import * as S from './file-cabinet.styles';

/**
 * One table presenter per view. Each keeps — and persists, under its own name — its sort, page size
 * and hidden columns, so a column hidden on received mail stays hidden there and nowhere else. The
 * app is in the name for the same reason as on the Roster: provider and support choices must never
 * cross.
 *
 * Two things are NOT persisted:
 * - **Filters.** The table writes them to localStorage on every fetch, the search box included — on
 *   this screen a full-text search over scanned mail and faxes, so patient names and member ids. The
 *   bucket is never read back, and logout does not clear it: PHI at rest, for nothing. Off, as on the
 *   task list (`message-list-panel.tsx`).
 * - **The page number.** A tab opens on page one: restoring "page 4" under a fresh search or a
 *   different account shows an empty page beside a non-zero total.
 */
export function createFileCabinetPresenter(
    view: FileCabinetViewKey,
    app: FileCabinetApp,
    documentTypes: string[],
    capabilities: FileCabinetCapabilities,
    context: FileCabinetCellContext = {},
): DataTablePresenter<FileCabinetRecord> {
    const def = FILE_CABINET_VIEWS[view] as FileCabinetView;
    const columns: ITableColumn<FileCabinetRecord>[] = def.columns.map((column, index) => ({
        id: column.key,
        Header: column.label,
        // Cells render from the whole record; the accessor only gives react-table a stable value.
        accessor: (record: FileCabinetRecord): string => fileCabinetRecordId(view, record) ?? '',
        width: column.width,
        show: column.hidden !== true,
        disableSortBy: !column.sortKey,
        defaultSort:
            column.key === def.defaultSort.columnKey
                ? { id: column.key, desc: def.defaultSort.order === 'DESCENDING' }
                : undefined,
        // The view's own keys: `def` is that view's entry, so its column keys are that view's.
        Cell: ({ row }: CellProps<FileCabinetRecord>): React.ReactElement => {
            const cell = renderFileCabinetCell(view, column.key as never, row.original as never, context);
            // The first column — the record's name or date — is a button: the table's row click has no tab
            // stop or key handler, so without it a keyboard could not open a single record (PR #671 review).
            return index === 0 && context.open ? (
                <S.RowOpenButton
                    type="button"
                    variant="unstyled"
                    onClick={(e: React.MouseEvent): void => {
                        // The row's own click would open it a second time.
                        e.stopPropagation();
                        context.open?.(row.original);
                    }}
                >
                    {cell}
                </S.RowOpenButton>
            ) : (
                cell
            );
        },
    }));

    const presenter = new DataTablePresenter<FileCabinetRecord>({
        name: `file_cabinet_${view}_${app}`,
        data: [],
        columns,
        filterSchemas: fileCabinetFilterSchemas(def, documentTypes, capabilities),
        filters: [],
    });
    presenter.saveFilters = (): void => undefined;
    const saveDefaults = presenter.saveTableDefaults;
    presenter.saveTableDefaults = (defaults): void => saveDefaults({ ...defaults, pageIndex: 0 });
    return presenter;
}
